import { LocalizedText } from "@/i18n/LocalizedText";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { authAPI } from "@/services/api";
import { applyActionCode } from "firebase/auth";
import { auth } from "@/firebase";
import { useAuth } from "@/components/AuthContext";

type VerificationStatus = "resolving" | "applying" | "continuing" | "error";

type VerificationResult = { ok: true } | { ok: false; message: string };

const verificationByToken = new Map<string, Promise<VerificationResult>>();

const getOobCode = (redirectUrl: string) => {
  try {
    const parsed = new URL(redirectUrl);
    const mode = String(parsed.searchParams.get("mode") || "").trim();
    const oobCode = String(parsed.searchParams.get("oobCode") || "").trim();
    if (mode && mode !== "verifyEmail") return "";
    return oobCode;
  } catch {
    return "";
  }
};

const INVALID_LINK_MESSAGE = "Verification link is invalid or expired.";
const FAILED_LINK_MESSAGE =
  "We couldn't verify your email. The link may be expired or already used.";

const VerifyEmailLinkPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser, setEmailVerifiedState } = useAuth();
  const [status, setStatus] = useState<VerificationStatus>("resolving");
  const [errorMessage, setErrorMessage] = useState("");
  const continueStartedRef = useRef(false);
  const fallbackInProgressRef = useRef(false);

  const waitForBackendOnboardingSync = async () => {
    const delay = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const me = await authAPI.getMe();
        const onboardStep = Number(me?.user?.onboard_step || 0);
        if (onboardStep >= 1) {
          return me;
        }
      } catch {
        // Keep retrying until backend session/user state is visible.
      }
      await delay(250 * (attempt + 1));
    }
    return null;
  };

  const statusCopy = useMemo(() => {
    if (status === "resolving") return "Resolving your verification link...";
    if (status === "applying") return "Verifying your email...";
    if (status === "continuing")
      return "Email verified. Redirecting to onboarding...";
    return "";
  }, [status]);

  const continueToOnboarding = useCallback(async () => {
    if (continueStartedRef.current) return;
    continueStartedRef.current = true;
    setStatus("continuing");
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate("/login", { replace: true });
        return;
      }

      const idToken = await currentUser.getIdToken(true);
      if (!idToken) {
        navigate("/login", { replace: true });
        return;
      }

      await authAPI.login({ idToken });
      setEmailVerifiedState(true);
      const syncedMe = await waitForBackendOnboardingSync();
      await refreshUser().catch(() => undefined);
      const nextStep = Math.max(
        2,
        Math.min(4, Number(syncedMe?.user?.onboard_step || 1) + 1),
      );
      navigate(`/register/${nextStep}`, { replace: true });
    } catch {
      navigate("/login", { replace: true });
    }
  }, [navigate, refreshUser, setEmailVerifiedState]);

  const continueIfAlreadyVerified = useCallback(async () => {
    if (fallbackInProgressRef.current) return false;
    fallbackInProgressRef.current = true;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return false;
      await currentUser.reload();
      if (!currentUser.emailVerified) return false;
      setEmailVerifiedState(true);
      setStatus("continuing");
      void continueToOnboarding();
      return true;
    } catch {
      return false;
    } finally {
      fallbackInProgressRef.current = false;
    }
  }, [continueToOnboarding, setEmailVerifiedState]);

  useEffect(() => {
    let active = true;

    const resolveToken = async () => {
      const normalizedToken = String(token || "").trim();
      if (!normalizedToken) {
        const continued = await continueIfAlreadyVerified();
        if (continued || !active) return;
        if (!active) return;
        setStatus("error");
        setErrorMessage(INVALID_LINK_MESSAGE);
        return;
      }

      let verificationPromise = verificationByToken.get(normalizedToken);
      if (!verificationPromise) {
        setStatus("applying");
        verificationPromise = (async (): Promise<VerificationResult> => {
          try {
            const result =
              await authAPI.resolveVerificationToken(normalizedToken);
            const redirectUrl = String(result?.redirectUrl || "").trim();
            if (!redirectUrl) {
              return { ok: false, message: INVALID_LINK_MESSAGE };
            }

            const oobCode = getOobCode(redirectUrl);
            if (!oobCode) {
              return { ok: false, message: INVALID_LINK_MESSAGE };
            }

            await applyActionCode(auth, oobCode);
            await auth.currentUser?.reload?.();
            if (auth.currentUser?.emailVerified) {
              setEmailVerifiedState(true);
            }
            return { ok: true };
          } catch {
            return { ok: false, message: FAILED_LINK_MESSAGE };
          }
        })().finally(() => {
          verificationByToken.delete(normalizedToken);
        });
        verificationByToken.set(normalizedToken, verificationPromise);
      } else {
        setStatus("resolving");
      }

      const verificationResult = await verificationPromise;
      if (!active) return;

      if (!verificationResult.ok) {
        const continued = await continueIfAlreadyVerified();
        if (continued || !active) return;
        if (!active) return;
        setStatus("error");
        setErrorMessage(verificationResult.message);
        return;
      }

      setStatus("continuing");
      void continueToOnboarding();
    };

    void resolveToken();

    return () => {
      active = false;
    };
  }, [
    token,
    continueIfAlreadyVerified,
    continueToOnboarding,
    setEmailVerifiedState,
  ]);

  return (
    <div className="w-full h-screen flex items-center justify-center px-6 bg-white">
      {status === "resolving" ||
      status === "applying" ||
      status === "continuing" ? (
        <div className="flex flex-col items-center text-center gap-4">
          <Spinner variant="circle-filled" size={40} />
          <p className="text-sm text-muted-foreground">{statusCopy}</p>
        </div>
      ) : (
        <div className="w-full max-w-md rounded-2xl border bg-white p-6 text-center shadow-sm">
          <h1 className="text-2xl font-semibold text-[#0A0A0A] mb-2">
            <LocalizedText id="legacy.verification.failed.1dccnof" />
          </h1>
          <p className="text-sm text-[#737373] mb-5">{errorMessage}</p>
          <Button
            onClick={() =>
              navigate("/register/1?verify=invalid", { replace: true })
            }
            className="hover:bg-[#ff8138] text-white bg-[#FF5E00] w-full"
          >
            <LocalizedText id="legacy.back.to.verification.step.qc438e" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default VerifyEmailLinkPage;
